// -----------------------------------------------------------------------------
//  I SERVIZI CHE UN PARTNER PUÒ CREARE — in un posto solo.
//
//  La lista e la regola che decide chi vede cosa servono sia a
//  `NuovaPrenotazionePage` sia all'Agenda (click su un giorno libero del
//  calendario): stanno qui per non avere due copie da allineare.
//
//  ⚠️ I nomi qui sono quelli che l'operatore usa, NON quelli del database.
//  Vedi le trappole di naming: `restaurant` = Notturno, `escort` = Transfer,
//  `journey` = Spostamento interno.
// -----------------------------------------------------------------------------

export const SERVIZI = [
  {
    value: 'pickup',
    label: 'Pickup & Delivery',
    desc: "Ritiriamo l'auto dal cliente e gliela riportiamo.",
  },
  {
    value: 'escort',
    label: 'Transfer',
    desc: 'Accompagniamo il cliente da un punto a un altro.',
  },
  {
    value: 'restaurant',
    label: 'Notturno',
    desc: 'Accompagniamo il cliente e lo riportiamo a casa.',
  },
  {
    value: 'journey',
    label: 'Spostamento interno',
    desc: "Sposta un'auto fra due tue sedi.",
  },
];

/**
 * Il partner può creare questo servizio?
 *
 * ⚠️ Pickup e Notturno si escludono: il backend rifiuta a un partner
 * `is_restaurant` i calcoli del pickup ordinario (403, `/pickup/range`) e
 * riserva ai soli partner notturni quelli del notturno
 * (`/pickup/range-restaurant`). Sono due listini diversi sullo stesso
 * servizio, non due servizi che convivono.
 */
export function abilitato(store, servizio) {
  if (servizio === 'pickup') return Boolean(store.bookingType && !store.restaurantType);
  if (servizio === 'restaurant') return Boolean(store.bookingType && store.restaurantType);
  if (servizio === 'escort') return Boolean(store.bookingType);
  if (servizio === 'journey') return Boolean(store.journeyType);
  return false;
}

/** I servizi attivi sul contratto, nell'ordine della lista. */
export const serviziAttivi = (store) => SERVIZI.filter((s) => abilitato(store, s.value));

/**
 * I servizi NON attivi.
 *
 * Non si nascondono: si dice perché. Nasconderli lascia l'operatore a
 * chiedersi se ha sbagliato pagina, e toglie a Simba l'occasione di venderli.
 */
export const serviziSpenti = (store) => SERVIZI.filter((s) => !abilitato(store, s.value));

/**
 * Dove si va per creare un servizio, eventualmente in una data già scelta.
 *
 * ⚠️ Lo spostamento interno NON passa da `/prenotazione/nuova`: ha un suo
 * editor già sviluppato (`JourneyPage`), che la data la prende dalla rotta e
 * quindi la mostra già impostata. Gli altri tre la ricevono in query string:
 * la pagina la mostra, ma il calendario dentro al form va ancora scelto a mano
 * — `DateSelectComponent` non ha una prop per impostarlo dall'esterno, e
 * aggiungergliela significa toccare i tre componenti di prenotazione.
 *
 * @param {string} servizio uno dei `SERVIZI[].value`
 * @param {string} [dataIso] data in forma `YYYY-MM-DD`
 */
export function rottaCreazione(servizio, dataIso) {
  if (servizio === 'journey') {
    // `null` nel percorso è il valore che JourneyPage già interpreta come
    // «nessuna data scelta»: non è una svista, è il suo contratto.
    return `/journey/${dataIso || 'null'}`;
  }
  const q = new URLSearchParams({ servizio });
  if (dataIso) q.set('data', dataIso);
  return `/prenotazione/nuova?${q.toString()}`;
}

/**
 * Dove si registra un cliente nuovo partendo da un form di prenotazione.
 *
 * La pagina Nuovo cliente legge `ritorno=prenotazione` e, a salvataggio
 * fatto, riporta al form dello stesso servizio (e della stessa data, se c'era).
 *
 * @param {string} [servizio] uno dei `SERVIZI[].value`
 * @param {string} [dataIso] data in forma `YYYY-MM-DD`
 * @param {string} [mail] l'email appena cercata nel form: la pagina la
 *   ritrova scritta, invece di farla ribattere
 */
export function rottaNuovoCliente(servizio, dataIso, mail) {
  const q = new URLSearchParams({ ritorno: 'prenotazione' });
  if (servizio) q.set('servizio', servizio);
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(dataIso || ''))) q.set('data', dataIso);
  if (String(mail || '').trim()) q.set('mail', String(mail).trim());
  return `/addcustomer_employee?${q.toString()}`;
}

export default { SERVIZI, abilitato, serviziAttivi, serviziSpenti, rottaCreazione, rottaNuovoCliente };
